import * as React from "react";

import { identity, maybe } from "../utils/functional";

import { buildAddressComponents } from "./utils";
import { eventValue } from "../utils/browser";
import usePlacesAutocomplete from "use-places-autocomplete";
import { useGate } from "statsig-react";
import { $ } from "kw/api";
import { useAPI } from "kw/api/useAPI";

function useQueryValidateAdressBz() {
  const { query } = useAPI();
  const queryValidateAdressBz = (address: string) => {
    return new Promise((resolve, reject) => {
      query(
        {
          validateAddressBz: [
            { address: $("address", "String!") },
            {
              data: true,
            },
          ],
        },
        ({ validateAddressBz: { data } }) => {
          resolve(JSON.parse(data));
        },
        {
          address,
        },
        (error) => {
          reject(error);
        }
      );
    });
  };
  return {
    queryValidateAdressBz,
  };
}

export function useGoogleAddressInput(args: TODO = {}): TODO {
  const { value: validateAddressInBzFlag } = useGate("k-44_validate_address_in_bz");

  const {
    value,
    onChange,
    error,
    onError,
    isValidating,
    onValidating = identity,
    country = "au",
    getClimateData,
    integrations,
  } = args;
  const {
    ready,
    suggestions: { loading: suggestionsLoading, data },
    setValue: setInputValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: [country],
      },
      types: ["address"],
    },
    debounce: 300,
  });
  const shouldValidateAddressInBZ =
    validateAddressInBzFlag && integrations?.includes("bz");

  const { queryValidateAdressBz } = useQueryValidateAdressBz();

  // Note, this only communicates failed validations; does not confirm passing validation.
  async function validateAddress(value) {
    if (!value) return value;
    maybe(onError)(null);
    onValidating(true);
    try {
      const [place, bzValidatedAddress] = await Promise.all([
        geocodePlaceId(value.placeId),
        shouldValidateAddressInBZ ? queryValidateAdressBz(value.description) : null,
      ]);

      const isBZAddressResponseEmpty =
        !bzValidatedAddress || Object.keys(bzValidatedAddress).length === 0;

      if (shouldValidateAddressInBZ && isBZAddressResponseEmpty) {
        throw new Error("Address could not be validated please select another address");
      }

      const climateData = getClimateData
        ? await getClimateData(place.formatted_address)
        : null; // to cater for those that do not use it
      const detailedAddress = {
        ...value,
        addressComponents: place.address_components,
        formattedAddress: place.formatted_address,
        // TODO: Should we call geometry.location.lat() and .lng() for actual number values here?
        geometry: place.geometry,
        climateData,
      };

      const bzAddressOverwrites = shouldValidateAddressInBZ
        ? {
            description: bzValidatedAddress.addressText,
            formattedAddress: bzValidatedAddress.addressText,
            postcode: bzValidatedAddress.postcode,
            state: bzValidatedAddress.state,
            streetLine: bzValidatedAddress.addressLine1,
            suburb: bzValidatedAddress.suburb,
          }
        : {};

      if (!getComponentValue(detailedAddress, "postal_code")) {
        throw new Error("Address must have postcode");
      }
      maybe(onChange)({
        ...detailedAddress,
        ...buildAddressComponents(detailedAddress),
        ...bzAddressOverwrites,
      });
    } catch (e) {
      clearSuggestions();
      maybe(onError)(e?.message);
    } finally {
      onValidating(false);
    }
  }

  async function handleChange(value) {
    if (value) {
      value = {
        id: value.place_id,
        description: value.description,
        placeId: value.place_id,
      };
    }
    maybe(onChange)(value);
    validateAddress(value);
  }

  React.useEffect(() => {
    if (value) {
      validateAddress(value);
    }
  }, []);

  return {
    onInputChange: eventValue(setInputValue),
    onChange: handleChange,
    disabled: !ready || isValidating,
    options: data,
    isLoading: suggestionsLoading || isValidating,
    error: !!error,
  };
}

function geocodePlaceId(placeId) {
  return new Promise<TODO>((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ placeId: placeId }, (results, status) => {
      if (status == "OK" && results[0]) {
        resolve(results[0]);
      }
      reject();
    });
  });
}

function getComponentValue(address, type) {
  const component =
    address.addressComponents.find((c) => c.types.find((t) => t == type)) || {};
  return component.short_name;
}
